import utils from "../../../../../utils/utils";

/** Default window for “Expiring soon” when no org threshold exists. */
export const EXPIRING_SOON_DAYS_DEFAULT = 30;

export const EXPIRING_SOON_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
];

export function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function startOfLocalDay(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Parse expiry as local calendar date (date-only safe). */
export function parseExpiryDate(raw) {
  if (raw == null || raw === "") return null;
  const str = String(raw).trim();
  const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }
  const d = utils.parseDateInput(str.slice(0, 10));
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function formatExpiryDisplay(raw) {
  const d = parseExpiryDate(raw);
  if (!d) return raw == null || raw === "" ? "—" : String(raw).slice(0, 10);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
}

/** Hide technical IMP-UNMAPPED placeholder from business UI. */
export function sanitizeImpProductLabel(code, name) {
  const c = String(code || "").trim();
  const n = String(name || "").trim();
  if (/^IMP-UNMAPPED$/i.test(c) || /^unmapped study kit$/i.test(n)) {
    return { code: "", name: "Study kit" };
  }
  if (!c && !n) return { code: "", name: "Study kit" };
  return { code: c, name: n || "Study kit" };
}

/**
 * Expiry day itself remains valid (expired when today is after expiry).
 */
export function isBatchExpired(row, today = startOfLocalDay()) {
  const expiry = parseExpiryDate(row?.ExpiryDate ?? row?.expiryDate);
  if (!expiry) return false;
  return today.getTime() > expiry.getTime();
}

export function isBatchExpiringSoon(
  row,
  days = EXPIRING_SOON_DAYS_DEFAULT,
  today = startOfLocalDay(),
) {
  const expiry = parseExpiryDate(row?.ExpiryDate ?? row?.expiryDate);
  if (!expiry) return false;
  if (isBatchExpired(row, today)) return false;
  const limit = new Date(today);
  limit.setDate(limit.getDate() + Number(days || EXPIRING_SOON_DAYS_DEFAULT));
  return expiry.getTime() <= limit.getTime();
}

export function isBatchRecalled(row) {
  return Boolean(row?.IsRecalled ?? row?.isRecalled);
}

export function batchAvailable(row) {
  return toNum(row?.AvailableOnHand ?? row?.availableOnHand);
}

export function batchHeld(row) {
  return toNum(row?.HeldQuantity ?? row?.heldQuantity);
}

export function batchOnHand(row) {
  return toNum(row?.OnHand ?? row?.onHand);
}

export function productKey(row) {
  return String(row?.ImpProductId || row?.impProductId || "").toLowerCase();
}

export function batchKey(row) {
  return String(row?.ImpBatchId || row?.impBatchId || "").toLowerCase();
}

export function trackingMethod(row) {
  const t = String(
    row?.InventoryTrackingMethod ||
      row?.inventoryTrackingMethod ||
      row?.trackingMethod ||
      "",
  ).trim();
  return t === "IndividualKit" ? "IndividualKit" : "BatchQuantity";
}

export function trackingLabel(method) {
  return method === "IndividualKit" ? "Individual Kit" : "Batch / Quantity";
}

export function buildProductMetaMap(productRows) {
  const map = new Map();
  (productRows || []).forEach((p) => {
    const id = String(p.ImpProductId || p.impProductId || "").toLowerCase();
    if (!id) return;
    map.set(id, {
      unitLabel: String(p.UnitLabel || p.unitLabel || "").trim(),
      dosageForm: String(p.DosageForm || p.dosageForm || "").trim(),
      strength: String(p.Strength || p.strength || "").trim(),
      strengthValue: p.StrengthValue ?? p.strengthValue ?? null,
      strengthUnit: String(p.StrengthUnit || p.strengthUnit || "").trim(),
      route: String(p.Route || p.route || "").trim(),
    });
  });
  return map;
}

export function enrichBalanceRows(balances, productMetaMap) {
  return (balances || []).map((row) => {
    const meta = productMetaMap.get(productKey(row)) || {};
    const strength =
      meta.strength ||
      (meta.strengthValue != null && meta.strengthUnit
        ? `${meta.strengthValue} ${meta.strengthUnit}`
        : "");
    return {
      ...row,
      UnitLabel: meta.unitLabel || row.UnitLabel || row.unitLabel || "",
      DosageForm: meta.dosageForm || row.DosageForm || row.dosageForm || "",
      Strength: strength || row.Strength || row.strength || "",
      Route: meta.route || row.Route || row.route || "",
    };
  });
}

export function quantityUnitLabel(row) {
  if (trackingMethod(row) === "IndividualKit") return "kits";
  const unit = String(row?.UnitLabel || row?.unitLabel || "").trim();
  return unit || "units";
}

export function formatQtyWithUnit(qty, row) {
  const n = toNum(qty);
  const unit = quantityUnitLabel(row);
  if (unit === "kits") {
    return `${n} ${n === 1 ? "kit" : "kits"}`;
  }
  return unit === "units" ? String(n) : `${n} ${unit}`;
}

export function batchCountLabel(count) {
  const n = toNum(count);
  return n === 1 ? "1 batch" : `${n} batches`;
}

export function kitCountLabel(count) {
  const n = toNum(count);
  return n === 1 ? "1 kit" : `${n} kits`;
}

/**
 * Scope summary: Available/Hold use ledger quantity (or kit count);
 * Expiring/Expired remain affected batch counts.
 * Quantity totals match IMP overview card sums for the same balance rows.
 */
export function buildScopeSummary(balances, heldRows, expiringDays) {
  const today = startOfLocalDay();
  const days = Number(expiringDays) || EXPIRING_SOON_DAYS_DEFAULT;
  const list = balances || [];

  const availableBatches = list.filter((r) => batchAvailable(r) > 0);
  const heldBatches = list.filter((r) => batchHeld(r) > 0);
  const expiringBatches = list.filter(
    (r) => batchOnHand(r) > 0 && isBatchExpiringSoon(r, days, today),
  );
  const expiredBatches = list.filter(
    (r) => batchOnHand(r) > 0 && isBatchExpired(r, today),
  );
  const recalledBatches = list.filter(
    (r) => batchOnHand(r) > 0 && isBatchRecalled(r),
  );

  const availableQuantity = availableBatches.reduce(
    (sum, r) => sum + batchAvailable(r),
    0,
  );
  const heldQuantity = heldBatches.reduce((sum, r) => sum + batchHeld(r), 0);

  const availableAllKits =
    availableBatches.length > 0 &&
    availableBatches.every((r) => trackingMethod(r) === "IndividualKit");
  const heldAllKits =
    heldBatches.length > 0 &&
    heldBatches.every((r) => trackingMethod(r) === "IndividualKit");

  const uniqueImps = new Set(list.map(productKey).filter(Boolean));

  return {
    impCount: uniqueImps.size,
    batchCount: list.length,
    availableQuantity,
    availableBatchCount: availableBatches.length,
    availableAllKits,
    heldQuantity,
    heldBatchCount: heldBatches.length,
    heldAllKits,
    heldReceiptCount: (heldRows || []).length,
    expiringBatchCount: expiringBatches.length,
    expiredBatchCount: expiredBatches.length,
    recalledBatchCount: recalledBatches.length,
    expiringDays: days,
  };
}

export function buildAttentionItems(summary, balances, heldRows, expiringDays) {
  const today = startOfLocalDay();
  const days = Number(expiringDays) || EXPIRING_SOON_DAYS_DEFAULT;
  const list = balances || [];
  const items = [];

  const expiredRows = list.filter(
    (r) => batchOnHand(r) > 0 && isBatchExpired(r, today),
  );
  if (expiredRows.length > 0) {
    const refs = expiredRows.slice(0, 3).map((r) => {
      const label = sanitizeImpProductLabel(
        r.ProductCode || r.productCode,
        r.ProductName || r.productName,
      );
      const code = label.code || label.name;
      const batch = r.BatchNumber || r.batchNumber || "";
      const exp = formatExpiryDisplay(r.ExpiryDate || r.expiryDate);
      return [code, batch].filter(Boolean).join(" / ") + (exp !== "—" ? ` · exp ${exp}` : "");
    });
    items.push({
      id: "expired",
      filter: "expired",
      label: "Expired stock remaining",
      count: expiredRows.length,
      countLabel: expiredRows.length === 1 ? "batch" : "batches",
      refs,
      actionLabel: "View expired batches",
      icon: "fas fa-calendar-times",
      tone: "danger",
      view: "batches",
    });
  }

  const expiringRows = list.filter(
    (r) => batchOnHand(r) > 0 && isBatchExpiringSoon(r, days, today),
  );
  if (expiringRows.length > 0) {
    const refs = expiringRows.slice(0, 3).map((r) => {
      const label = sanitizeImpProductLabel(
        r.ProductCode || r.productCode,
        r.ProductName || r.productName,
      );
      const code = label.code || label.name;
      const batch = r.BatchNumber || r.batchNumber || "";
      const exp = formatExpiryDisplay(r.ExpiryDate || r.expiryDate);
      return [code, batch].filter(Boolean).join(" / ") + (exp !== "—" ? ` · exp ${exp}` : "");
    });
    items.push({
      id: "expiring",
      filter: "expiring",
      label: "Approaching expiry",
      count: expiringRows.length,
      countLabel: expiringRows.length === 1 ? "batch" : "batches",
      refs,
      actionLabel: "View expiring batches",
      icon: "fas fa-hourglass-half",
      tone: "warning",
      view: "batches",
    });
  }

  const heldList = heldRows || [];
  const heldBatches = list.filter((r) => batchHeld(r) > 0);
  if (heldList.length > 0 || heldBatches.length > 0) {
    const refSource = heldList.length > 0 ? heldList : heldBatches;
    const refs = refSource.slice(0, 3).map((r) => {
      const code = r.ProductCode || r.productCode || "";
      const batch = r.BatchNumber || r.batchNumber || "";
      const reason =
        r.ReviewReason ||
        r.reviewReason ||
        r.ReceiptStatus ||
        r.receiptStatus ||
        "Requires review";
      return (
        [code, batch].filter(Boolean).join(" / ") +
        (reason ? ` · ${reason}` : "")
      );
    });
    const count = Math.max(heldBatches.length, heldList.length);
    items.push({
      id: "held",
      filter: "held",
      label: "Hold / requires review",
      count,
      countLabel: count === 1 ? "item" : "items",
      refs,
      actionLabel: "Review held stock",
      icon: "fas fa-pause-circle",
      tone: "review",
      view: "held",
    });
  }

  const recalledRows = list.filter(
    (r) => batchOnHand(r) > 0 && isBatchRecalled(r),
  );
  if (recalledRows.length > 0) {
    const refs = recalledRows.slice(0, 3).map((r) => {
      const code = r.ProductCode || r.productCode || "";
      const batch = r.BatchNumber || r.batchNumber || "";
      const reason = r.RecallReason || r.recallReason || "Recalled";
      return [code, batch].filter(Boolean).join(" / ") + ` · ${reason}`;
    });
    items.push({
      id: "recalled",
      filter: "recalled",
      label: "Recalled stock",
      count: recalledRows.length,
      countLabel: recalledRows.length === 1 ? "batch" : "batches",
      refs,
      actionLabel: "View recalled batches",
      icon: "fas fa-exclamation-triangle",
      tone: "danger",
      view: "batches",
    });
  }

  return items;
}

/** Cap overview cards so the dashboard stays scannable. */
export const DASHBOARD_IMP_CARD_LIMIT = 6;

function nearestRelevantExpiry(batches, today = startOfLocalDay()) {
  let best = null;
  let bestTs = null;
  (batches || []).forEach((row) => {
    const expiry = parseExpiryDate(row.ExpiryDate ?? row.expiryDate);
    if (!expiry) return;
    const ts = expiry.getTime();
    if (bestTs == null || ts < bestTs) {
      bestTs = ts;
      best = row.ExpiryDate ?? row.expiryDate;
    }
  });
  if (best == null) return { raw: null, expired: false, soon: false };
  const probe = { ExpiryDate: best };
  return {
    raw: best,
    expired: isBatchExpired(probe, today),
    soon: isBatchExpiringSoon(probe, EXPIRING_SOON_DAYS_DEFAULT, today),
  };
}

export function groupBalancesByImp(balances, expiringDays = EXPIRING_SOON_DAYS_DEFAULT) {
  const map = new Map();
  const days = Number(expiringDays) || EXPIRING_SOON_DAYS_DEFAULT;
  (balances || []).forEach((row) => {
    const id = productKey(row);
    if (!id) return;
    if (!map.has(id)) {
      const label = sanitizeImpProductLabel(
        row.ProductCode || row.productCode,
        row.ProductName || row.productName,
      );
      map.set(id, {
        impProductId: row.ImpProductId || row.impProductId,
        productCode: label.code,
        productName: label.name,
        trackingMethod: trackingMethod(row),
        unitLabel: row.UnitLabel || row.unitLabel || "",
        dosageForm: row.DosageForm || row.dosageForm || "",
        strength: row.Strength || row.strength || "",
        route: row.Route || row.route || "",
        batches: [],
      });
    }
    const g = map.get(id);
    g.batches.push(row);
  });

  const today = startOfLocalDay();
  return Array.from(map.values()).map((g) => {
    const available = g.batches.reduce((s, r) => s + batchAvailable(r), 0);
    const held = g.batches.reduce((s, r) => s + batchHeld(r), 0);
    const onHand = g.batches.reduce((s, r) => s + batchOnHand(r), 0);

    let expiredQty = 0;
    let expiringQty = 0;
    let heldSegQty = 0;
    let availableSegQty = 0;
    g.batches.forEach((r) => {
      const oh = batchOnHand(r);
      if (oh <= 0) return;
      if (isBatchExpired(r, today)) {
        expiredQty += oh;
        return;
      }
      if (isBatchExpiringSoon(r, days, today)) {
        expiringQty += oh;
        return;
      }
      heldSegQty += batchHeld(r);
      availableSegQty += batchAvailable(r);
    });

    const expiry = nearestRelevantExpiry(g.batches, today);
    const storages = new Set();
    g.batches.forEach((r) => {
      const code = String(r.StorageUnitCode || r.storageUnitCode || "").trim();
      const name = String(r.StorageUnitName || r.storageUnitName || "").trim();
      const label = [code, name].filter(Boolean).join(" — ");
      if (label) storages.add(label);
    });
    return {
      ...g,
      available,
      held,
      onHand,
      expiredQty,
      expiringQty,
      availableSegQty,
      heldSegQty,
      batchCount: g.batches.length,
      nearestExpiry: expiry.raw,
      nearestExpiryExpired: expiry.expired,
      nearestExpirySoon: expiry.soon,
      hasHeld: held > 0,
      hasRecalled: g.batches.some(isBatchRecalled),
      hasExpired: expiredQty > 0,
      hasExpiringSoon: expiringQty > 0,
      storageSummary:
        storages.size === 0
          ? "—"
          : storages.size === 1
            ? Array.from(storages)[0]
            : "Multiple locations",
      storageCount: storages.size,
    };
  });
}

export function filterBalanceRows(rows, filterKey, expiringDays) {
  const today = startOfLocalDay();
  const days = Number(expiringDays) || EXPIRING_SOON_DAYS_DEFAULT;
  const list = rows || [];
  switch (filterKey) {
    case "available":
      return list.filter((r) => batchAvailable(r) > 0);
    case "held":
      return list.filter((r) => batchHeld(r) > 0);
    case "expiring":
      return list.filter(
        (r) => batchOnHand(r) > 0 && isBatchExpiringSoon(r, days, today),
      );
    case "expired":
      return list.filter((r) => batchOnHand(r) > 0 && isBatchExpired(r, today));
    case "recalled":
      return list.filter((r) => batchOnHand(r) > 0 && isBatchRecalled(r));
    default:
      return list;
  }
}

/** Client-side search for modal tables (IMP / batch / storage). */
export function searchInventoryRows(rows, search) {
  const term = String(search || "")
    .trim()
    .toLowerCase();
  if (!term) return rows || [];
  return (rows || []).filter((r) => {
    const hay = [
      r.ProductCode,
      r.productCode,
      r.ProductName,
      r.productName,
      r.BatchNumber,
      r.batchNumber,
      r.KitNumber,
      r.kitNumber,
      r.StorageUnitCode,
      r.storageUnitCode,
      r.StorageUnitName,
      r.storageUnitName,
      r.ReviewReason,
      r.reviewReason,
      r.TxnType,
      r.txnType,
    ]
      .map((x) => String(x || "").toLowerCase())
      .join(" ");
    return hay.includes(term);
  });
}

export function sortBalanceRows(rows, sortKey) {
  const list = [...(rows || [])];
  if (sortKey === "expiry") {
    list.sort((a, b) => {
      const da = parseExpiryDate(a.ExpiryDate ?? a.expiryDate);
      const db = parseExpiryDate(b.ExpiryDate ?? b.expiryDate);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.getTime() - db.getTime();
    });
    return list;
  }
  if (sortKey === "product") {
    list.sort((a, b) =>
      String(a.ProductName || a.productName || "").localeCompare(
        String(b.ProductName || b.productName || ""),
      ),
    );
    return list;
  }
  return list;
}

export function filterImpGroups(groups, { search, tracking, attention, showZero }) {
  const term = String(search || "")
    .trim()
    .toLowerCase();
  return (groups || []).filter((g) => {
    if (!showZero && toNum(g.onHand) === 0) return false;
    if (tracking === "IndividualKit" && g.trackingMethod !== "IndividualKit") {
      return false;
    }
    if (tracking === "BatchQuantity" && g.trackingMethod !== "BatchQuantity") {
      return false;
    }
    if (attention === "held" && !g.hasHeld) return false;
    if (attention === "expired" && !g.hasExpired) return false;
    if (attention === "expiring" && !g.hasExpiringSoon) return false;
    if (attention === "recalled" && !g.hasRecalled) return false;
    if (attention === "available" && !(toNum(g.available) > 0)) return false;
    if (!term) return true;
    const batchHay = (g.batches || [])
      .map((b) => String(b.BatchNumber || b.batchNumber || ""))
      .join(" ")
      .toLowerCase();
    const hay = `${g.productCode} ${g.productName} ${batchHay}`.toLowerCase();
    return hay.includes(term);
  });
}

export function sortImpGroups(groups, sortKey) {
  const list = [...(groups || [])];
  if (sortKey === "expiry") {
    list.sort((a, b) => {
      const da = parseExpiryDate(a.nearestExpiry);
      const db = parseExpiryDate(b.nearestExpiry);
      if (!da && !db) return a.productName.localeCompare(b.productName);
      if (!da) return 1;
      if (!db) return -1;
      return da.getTime() - db.getTime();
    });
    return list;
  }
  list.sort((a, b) =>
    String(a.productName || "").localeCompare(String(b.productName || "")),
  );
  return list;
}

export function filterLabel(filterKey, expiringDays) {
  switch (filterKey) {
    case "available":
      return "Available batches";
    case "held":
      return "Hold / review";
    case "expiring":
      return `Expiring within ${expiringDays || EXPIRING_SOON_DAYS_DEFAULT} days`;
    case "expired":
      return "Expired stock";
    case "recalled":
      return "Recalled";
    case "all":
    default:
      return "All on-hand inventory";
  }
}

function batchIdOf(row) {
  return String(row?.ImpBatchId || row?.impBatchId || "").trim();
}

function sortBatchesFefo(a, b) {
  const da = parseExpiryDate(a.ExpiryDate || a.expiryDate);
  const db = parseExpiryDate(b.ExpiryDate || b.expiryDate);
  if (da && db) return da - db;
  if (da) return -1;
  if (db) return 1;
  return 0;
}

/**
 * Pick a preferred ImpBatchId from inventory card batches for action overlays.
 * @param {"available"|"onHand"|"heldFirst"} mode
 */
export function pickPreferredBatchId(batches, mode = "onHand") {
  const list = (batches || []).filter((b) => {
    if (mode === "available") return batchAvailable(b) > 0;
    return batchOnHand(b) > 0;
  });
  if (!list.length) return null;

  let pool = list;
  if (mode === "heldFirst") {
    const held = list.filter((b) => batchHeld(b) > 0);
    if (held.length) pool = held;
  }

  const sorted = [...pool].sort(sortBatchesFefo);
  const id = batchIdOf(sorted[0]);
  return id || null;
}

/** Match storage unit (fridge) dropdown value from balance location. */
export function matchStorageUnitIdFromBatch(units, batchRow) {
  const byId = String(
    batchRow?.StorageUnitId || batchRow?.storageUnitId || "",
  ).trim();
  if (byId) {
    const hitById = (units || []).find(
      (u) =>
        String(u.StorageUnitId || u.storageUnitId || "")
          .trim()
          .toLowerCase() === byId.toLowerCase(),
    );
    if (hitById) {
      return String(hitById.StorageUnitId || hitById.storageUnitId || "");
    }
  }

  const code = String(
    batchRow?.StorageUnitCode || batchRow?.storageUnitCode || "",
  )
    .trim()
    .toLowerCase();
  if (!code) return "";
  const hit = (units || []).find(
    (u) =>
      String(u.UnitCode || u.unitCode || "")
        .trim()
        .toLowerCase() === code,
  );
  return hit ? String(hit.StorageUnitId || hit.storageUnitId || "") : "";
}

/** Match storage rack dropdown value from balance location (under a fridge). */
export function matchStorageRackIdFromBatch(racks, batchRow, storageUnitId = "") {
  const unitId = String(storageUnitId || "").trim().toLowerCase();
  const pool = (racks || []).filter((r) => {
    if (!unitId) return true;
    return (
      String(r.StorageUnitId || r.storageUnitId || "")
        .trim()
        .toLowerCase() === unitId
    );
  });

  const byId = String(
    batchRow?.StorageRackId || batchRow?.storageRackId || "",
  ).trim();
  if (byId) {
    const hitById = pool.find(
      (r) =>
        String(r.StorageRackId || r.storageRackId || "")
          .trim()
          .toLowerCase() === byId.toLowerCase(),
    );
    if (hitById) {
      return String(hitById.StorageRackId || hitById.storageRackId || "");
    }
  }

  const code = String(
    batchRow?.StorageRackCode || batchRow?.storageRackCode || "",
  )
    .trim()
    .toLowerCase();
  if (!code) return "";
  const hit = pool.find(
    (r) =>
      String(r.RackCode || r.rackCode || "")
        .trim()
        .toLowerCase() === code,
  );
  return hit ? String(hit.StorageRackId || hit.storageRackId || "") : "";
}

/** Suggest stock-return reason from batch condition. */
export function suggestStockReturnReason(batchRow) {
  if (!batchRow) return "";
  if (isBatchRecalled(batchRow)) return "Recall";
  if (isBatchExpired(batchRow)) return "Expired";
  if (batchHeld(batchRow) > 0) return "RejectedAfterReview";
  return "";
}
